import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryParam, StringParam } from 'use-query-params';

import { isDefined, localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { getVenueInventory } from 'modules/shared-menu/api';
import { SelectableOfferFilterId } from 'modules/shared-menu/components/FilterMenu/types';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import { useVenueSelectionOverrides } from './useSelectionOverrides';
import { useSelectionData } from '../../../hooks/useSelectionData';
import { SelectionCategory } from '../../../types';
import { useWoltSpecials } from '../context/WoltSpecialsContext';
import { Offer, SharedMenuElement, SharedMenuOffer, Option } from '../types';
import {
  checkIfItemIsAvailable,
  getOfferCategory,
  flattenCategories,
  getParentCategory,
  isSharedMenuOffer,
  isSharedMenuOption,
  getOfferOverrides,
  applyOverridesToOffer,
} from '../utils';

export type ListContentType =
  | 'all-items'
  | 'all-options'
  | 'all-disabled'
  | 'items-by-category'
  | 'items-by-subcategory'
  | 'items-with-no-category';

const selectableFilterIdToRulesMap: Record<SelectableOfferFilterId, (item: Offer) => boolean> = {
  WITH_IMAGES: (item: Offer) => !!(item?.image || item?.images?.[0]?.url),
  WITHOUT_IMAGES: (item: Offer) => !item?.image && (!item?.images || item?.images?.length === 0),
  DISABLED: (item: Offer) => !item.enabled.enabled,
  ACTIVE: (item: Offer) => checkIfItemIsAvailable(item.enabled),
  INACTIVE: (item: Offer) => !checkIfItemIsAvailable(item.enabled),
  USE_DISCOUNTED_PRICE: (item: Offer) => item?.discounted?.discount_type === 'absolute',
  USE_PERCENTAGE: (item: Offer) => item?.discounted?.discount_type === 'relative',
  WITHOUT_DISCOUNT: (item: Offer) => !item?.discounted,
  SOLD_OUT: (item: Offer) => item.inventory_mode === 'forced_out_of_stock',
  // TODO: Add rules for this when we have `purchasabilityErrors`
  CANNOT_BE_ORDERED: (_item: Offer) => true,
  CONNECTED: (item: Offer) => !!item.product_id,
  NOT_CONNECTED: (item: Offer) => !item.product_id,
};

const convertPriceToLocaleString = (amount: number, currency: Currency) =>
  amount.toLocaleString(undefined, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
  });

const buildItemFilter =
  (filter: string, locale: string, currency: Currency, selectableFilterIds: SelectableOfferFilterId[] = []) =>
  (item: Offer) => {
    const query = filter.trim().toLocaleLowerCase();
    const name = localize(item.name, locale).toLocaleLowerCase();
    const description = localize(item.description ?? [], locale).toLocaleLowerCase();
    const originalPrice = item.discounted
      ? convertPriceToLocaleString(item.discounted.original_price, currency)
      : undefined;
    const basePrice = convertPriceToLocaleString(item.price, currency);
    const gtin = item.barcode_gtin;
    const sku = item.merchant_sku?.toLocaleLowerCase();
    const posId = item.external_id?.toLocaleLowerCase();

    const searchMatches = [name, description, originalPrice, basePrice, gtin, sku, posId].some(value =>
      value?.includes(query),
    );

    const filterMatches = selectableFilterIds.every(selectableFilterId =>
      selectableFilterIdToRulesMap?.[selectableFilterId]?.(item),
    );

    return searchMatches && filterMatches;
  };

const buildOptionFilter = (filter: string, locale: string, items: Offer[]) => (option: Option) => {
  const query = filter.trim().toLocaleLowerCase();
  const name = localize(option.name, locale).toLocaleLowerCase();
  const valueNames = option.values.map(value => localize(value.name, locale).toLocaleLowerCase());
  const linkedItemNames = items
    .filter(item => item.option_bindings?.find(ob => ob.option_id === option.id))
    .map(item => localize(item.name, locale).toLocaleLowerCase());
  return (
    name.includes(query) ||
    valueNames.some(valueName => valueName.includes(query)) ||
    linkedItemNames.some(itemName => itemName.includes(query))
  );
};

const getItemStatesForCategory = (offers: Offer[], category: SelectionCategory) =>
  (category.offer_bindings ?? []).map(binding => offers.find(item => item.id === binding.offer_id)).filter(isDefined);

const getItemStatesWithNoCategory = (offers: Offer[], categories: SelectionCategory[]) =>
  offers.filter(item => !getOfferCategory(categories, item.id));

const buildItemsList = (
  offers: Offer[],
  category: SelectionCategory | null,
  headerType?: 'category-subheader' | 'subcategory-subheader',
  parentCategory?: SelectionCategory,
): SharedMenuElement[] => {
  if (offers.length === 0) {
    return [];
  }

  const offersList = offers.map(offer => ({ type: 'item' as const, itemState: offer, category }));

  return headerType
    ? [
        {
          type: headerType,
          category,
          parentCategory,
        },
        ...offersList,
      ]
    : offersList;
};

const buildOptionsList = (optionStates: Option[], header?: string): SharedMenuElement[] => {
  if (optionStates.length === 0) {
    return [];
  }
  const optionsList = optionStates.map(optionState => ({ type: 'option' as const, optionState }));
  return header ? [{ type: 'options-subheader', name: header }, ...optionsList] : optionsList;
};

export const getNewOfferInitialCategoryId = (
  offerToCopyCategoryId?: string,
  categoryId?: string,
  currentCategoryHasSubcategories?: boolean,
) => {
  if (offerToCopyCategoryId) {
    return offerToCopyCategoryId;
  }

  if (!currentCategoryHasSubcategories) {
    return categoryId ?? null;
  }

  return null;
};

const useSharedMenuEditorLists = (
  listContentType: ListContentType,
  settings?: {
    showWoltSpecials?: boolean;
    categoryId?: string;
    hasSubcategories?: boolean;
  },
) => {
  const { currency } = useCountryConfigContext();
  const { selectionOverrides } = useVenueSelectionOverrides();
  const { showWoltSpecials, categoryId, hasSubcategories } = settings || {
    showWoltSpecials: false,
    categoryId: undefined,
    hasSubcategories: false,
  };
  const {
    selection,
    categories: allCategories,
    offers: allOffers,
    options: allOptions,
    updateSelectionData,
  } = useSelectionData();

  const { vlmeConfig } = useSelectionInfoContext();
  const isSingleVenue = !!vlmeConfig?.isSingleVenue;
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;

  const allMaybeOverriddenOffers = useMemo(() => {
    if (
      !isSharedVenue ||
      !selectionOverrides ||
      Object.keys(selectionOverrides).length === 0 ||
      Object.keys(selectionOverrides.offers).length === 0
    ) {
      return allOffers;
    }

    return allOffers.map(offer => {
      const offerOverrides = getOfferOverrides(offer, selectionOverrides);
      return applyOverridesToOffer(offer, offerOverrides);
    });
  }, [allOffers, isSharedVenue, selectionOverrides]);

  const intl = useIntl();

  const [activeItemId, setActiveItemId] = useQueryParam('item', StringParam);
  const [activeOptionId, setActiveOptionId] = useQueryParam('option', StringParam);
  const [searchFilter, setSearchFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<SelectableOfferFilterId[]>([]);
  const [itemElementToCopy, setItemElementToCopy] = useState<SharedMenuOffer>();
  const [optionToCopy, setOptionToCopy] = useState<Option>();
  const scope = useScopeContext();
  const { woltSpecialsCategory } = useWoltSpecials();

  const { data: offerInventory, refetch: refetchOfferInventory } = useQuery(
    [`offer-inventories/${scope.id}`],
    () => getVenueInventory(scope.id),
    {
      enabled: !!vlmeConfig,
      refetchInterval: 30000,
    },
  );

  const categories = useMemo(
    () => (showWoltSpecials ? [...allCategories] : [...allCategories].filter(category => !category.is_wolt_special)),
    [allCategories, showWoltSpecials],
  );

  const flattenedCategories = useMemo(() => flattenCategories(categories), [categories]);

  const itemFilterFn = useMemo(
    () => buildItemFilter(searchFilter, selection.primary_language, currency, appliedFilters),
    [currency, searchFilter, selection.primary_language, appliedFilters],
  );

  const optionFilterFn = useMemo(
    () => buildOptionFilter(searchFilter, selection.primary_language, [...allMaybeOverriddenOffers]),
    [searchFilter, selection.primary_language, allMaybeOverriddenOffers],
  );

  const noCategoryItems = useMemo(() => {
    const itemStatesWithoutCategory = getItemStatesWithNoCategory(allMaybeOverriddenOffers, allCategories).filter(
      itemFilterFn,
    );
    return [...buildItemsList(itemStatesWithoutCategory, null, 'category-subheader')];
  }, [allMaybeOverriddenOffers, allCategories, itemFilterFn]);

  const menuEditorList = useMemo<SharedMenuElement[]>(() => {
    switch (listContentType) {
      case 'all-items': {
        const categoryItems = flattenedCategories.reduce<SharedMenuElement[]>((acc, category) => {
          const itemStates = getItemStatesForCategory(allMaybeOverriddenOffers, category).filter(itemFilterFn);
          return [
            ...acc,
            ...buildItemsList(
              itemStates,
              category,
              'category-subheader',
              getParentCategory(category.id, categories) ?? undefined,
            ),
          ];
        }, []);
        return [...categoryItems, ...noCategoryItems];
      }

      case 'all-options': {
        const filteredOptions = allOptions.filter(optionFilterFn);
        return buildOptionsList(filteredOptions);
      }

      case 'all-disabled': {
        const categoryItems = flattenedCategories.reduce<SharedMenuElement[]>((acc, category) => {
          const itemStates = getItemStatesForCategory(allMaybeOverriddenOffers, category)
            .filter(itemFilterFn)
            .filter(item => !item.enabled.enabled);
          return [
            ...acc,
            ...buildItemsList(
              itemStates,
              category,
              'category-subheader',
              getParentCategory(category.id, categories) ?? undefined,
            ),
          ];
        }, []);
        const itemStatesWithoutCategory = getItemStatesWithNoCategory(allMaybeOverriddenOffers, allCategories)
          .filter(itemFilterFn)
          .filter(item => !item.enabled.enabled);
        const noCategoryItems = [...buildItemsList(itemStatesWithoutCategory, null, 'category-subheader')];
        const options = allOptions
          .filter(optionFilterFn)
          .filter(option => option.values.find(optionValue => !optionValue.enabled.enabled));
        const optionsList = buildOptionsList(options, intl.formatMessage({ id: 'menu.optionsWithDisabledChoices' }));
        return [...categoryItems, ...noCategoryItems, ...optionsList];
      }

      case 'items-with-no-category':
      case 'items-by-category': {
        const category = allCategories.find(category => category.id === categoryId) || null;

        if (!category?.subcategories?.length) {
          const itemStates = category
            ? getItemStatesForCategory(allMaybeOverriddenOffers, category)
            : getItemStatesWithNoCategory(allMaybeOverriddenOffers, allCategories);
          const filteredItems = itemStates.filter(itemFilterFn);
          return buildItemsList(filteredItems, category);
        }

        const categoryItems = category.subcategories.reduce<SharedMenuElement[]>((acc, subcategory) => {
          const itemStates = getItemStatesForCategory(allMaybeOverriddenOffers, subcategory).filter(itemFilterFn);
          const itemsList = buildItemsList(itemStates, subcategory, 'subcategory-subheader');
          if (!!itemsList.length) {
            return [...acc, ...itemsList];
          }
          return [
            ...acc,
            {
              type: 'subcategory-subheader',
              name: localize(subcategory.name, selection.primary_language),
              category: subcategory,
              isSubcategory: true,
            },
          ];
        }, []);

        return categoryItems;
      }

      case 'items-by-subcategory': {
        const category = flattenCategories(allCategories).find(category => category.id === categoryId) || null;

        const itemStates = category
          ? getItemStatesForCategory(allMaybeOverriddenOffers, category)
          : getItemStatesWithNoCategory(allMaybeOverriddenOffers, allCategories);
        const filteredItems = itemStates.filter(itemFilterFn);
        return buildItemsList(filteredItems, category);
      }
    }
  }, [
    allCategories,
    allMaybeOverriddenOffers,
    allOptions,
    itemFilterFn,
    categories,
    categoryId,
    flattenedCategories,
    intl,
    listContentType,
    noCategoryItems,
    optionFilterFn,
    selection.primary_language,
  ]);

  const activeItem = useMemo(
    () => (activeItemId ? allOffers.find(item => item.id === activeItemId) : undefined),
    [activeItemId, allOffers],
  );

  const activeOption = useMemo(
    () => allOptions.find(option => option.id === activeOptionId),
    [activeOptionId, allOptions],
  );

  const handleItemCopy = useCallback(
    (itemId: string, isSpecial?: boolean) => {
      const itemElementToCopy = menuEditorList.filter(isSharedMenuOffer).find(item => item.itemState.id === itemId);
      setItemElementToCopy(itemElementToCopy);

      const isDuplicatingWoltSpecialsOffer =
        typeof itemElementToCopy?.category?.id === 'string' &&
        itemElementToCopy?.category.id === woltSpecialsCategory?.id;

      const offerTypeToCreate = isSpecial || isDuplicatingWoltSpecialsOffer ? 'new-special' : 'new-item';

      setActiveItemId(offerTypeToCreate);
    },
    [menuEditorList, setActiveItemId, woltSpecialsCategory?.id],
  );

  const handleOptionCopy = useCallback(
    (optionId: string) => {
      const optionElementToCopy = menuEditorList
        .filter(isSharedMenuOption)
        .find(option => option.optionState.id === optionId);
      setOptionToCopy(optionElementToCopy?.optionState);
      setActiveOptionId('new-option');
    },
    [menuEditorList, setActiveOptionId],
  );

  const newOptionSidePanelProps = useMemo(
    () => ({
      optionToCopy,
      onSuccess: (option: Option) => {
        updateSelectionData({ actionType: 'addOption', payload: option });
        setActiveOptionId(undefined);
        setOptionToCopy(undefined);
      },
      isOpen: activeOptionId === 'new-option',
      onClose: () => {
        setActiveOptionId(prevId => (!prevId || prevId === 'new-option' ? undefined : prevId));
        setOptionToCopy(undefined);
      },
    }),
    [activeOptionId, optionToCopy, setActiveOptionId, updateSelectionData],
  );

  const isCreatingNewSpecial = useMemo(
    () => activeItemId === 'new-special' && !!woltSpecialsCategory,
    [activeItemId, woltSpecialsCategory],
  );

  const newItemSidePanelProps = useMemo(
    () => ({
      isOpen: activeItemId === 'new-item' || isCreatingNewSpecial,
      itemToCopy: itemElementToCopy?.itemState,
      initialCategoryId: isCreatingNewSpecial
        ? (woltSpecialsCategory as SelectionCategory)?.id
        : getNewOfferInitialCategoryId(itemElementToCopy?.category?.id, categoryId, hasSubcategories),
      onSuccess: () => {
        if (isSingleVenue) {
          refetchOfferInventory();
        }
        setActiveItemId(undefined);
        setItemElementToCopy(undefined);
      },
      onClose: () => {
        setActiveItemId(undefined);
        setItemElementToCopy(undefined);
      },
    }),
    [
      activeItemId,
      isCreatingNewSpecial,
      itemElementToCopy?.itemState,
      itemElementToCopy?.category?.id,
      woltSpecialsCategory,
      categoryId,
      hasSubcategories,
      isSingleVenue,
      setActiveItemId,
      refetchOfferInventory,
    ],
  );

  return {
    menuEditorList,
    activeItem,
    activeOption,
    searchFilter,
    newItemSidePanelProps,
    newOptionSidePanelProps,
    setActiveItemId,
    setActiveOptionId,
    setSearchFilter,
    appliedFilters,
    setAppliedFilters,
    handleItemCopy,
    handleOptionCopy,
    offerInventory,
    refetchOfferInventory,
  };
};

export default useSharedMenuEditorLists;
